import React, { useMemo, useEffect, useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Scenario } from '../../types/app';
import { ChartDataPoint } from '../../types/app';
import { SCENARIO_COLORS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';
import { dlog, debugEnabled } from '../../utils/debug';
// Chart legend moved to Scenario list cards

interface NetWorthChartProps {
  scenarios: Scenario[];
  timeHorizonYears: number;
  className?: string;
}

export const NetWorthChart: React.FC<NetWorthChartProps> = ({
  scenarios,
  timeHorizonYears,
  className = '',
}) => {

  // Phase 7.1: Track chart dependencies
  const scenariosRef = useRef(scenarios);
  const timeHorizonRef = useRef(timeHorizonYears);
  const visibleIdsRef = useRef('');
  const baselineIdRef = useRef('');

  useEffect(() => {
    const visibleIds = scenarios.filter(s => s.visible !== false && s.analysisResult?.yearly_data).map(s => s.id).join(',');
    const baselineId = scenarios.find(s => s.isBaseline)?.id || '';
    
    console.log('[CHART_DEPS_CHANGE]', {
      scenariosChanged: scenariosRef.current !== scenarios,
      timeHorizonChanged: timeHorizonRef.current !== timeHorizonYears,
      visibleIdsChanged: visibleIdsRef.current !== visibleIds,
      baselineIdChanged: baselineIdRef.current !== baselineId,
      visibleIds,
      baselineId
    });
    
    scenariosRef.current = scenarios;
    timeHorizonRef.current = timeHorizonYears;
    visibleIdsRef.current = visibleIds;
    baselineIdRef.current = baselineId;
  }, [scenarios, timeHorizonYears]);

  // Deterministic scenario color util removed (not used here)

  // Export chart data
  const handleExport = () => {
    const csvData = chartData.map(point => {
      const row: any = { Year: point.year };
      scenarios.forEach(scenario => {
        if (scenario.analysisResult?.yearly_data) {
          row[`${scenario.title} (${scenario.scenarioType})`] = point[scenario.id];
        }
      });
      return row;
    });

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scenario-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Prepare chart data with relative values vs baseline
  const chartData: ChartDataPoint[] = useMemo(() => {
    if (debugEnabled) dlog('[Chart] Recalculating chart data', {
      scenarioCount: scenarios.length,
      scenariosWithResults: scenarios.filter(s => s.analysisResult).length,
      rentingScenarios: scenarios.filter(s => s.scenarioType === 'renting').map(s => ({
        title: s.title,
        hasResult: !!s.analysisResult,
        rentParam: s.parameters?.renting_params?.current_rent_living_alone
      })),
      buyingScenarios: scenarios.filter(s => s.scenarioType === 'buying').map(s => ({
        title: s.title,
        hasResult: !!s.analysisResult,
        buyingRentalIncome: s.parameters?.buying_params?.rental_strategy?.monthly_income,
        finalBuyingNW: s.analysisResult?.final_buying_net_worth
      }))
    });
    
    const data: ChartDataPoint[] = [];
    const visibleScenarios = scenarios.filter(s => (s.visible !== false) && s.analysisResult?.yearly_data);
    const baselineScenario = scenarios.find(s => s.isBaseline);
    
    if (!baselineScenario || !baselineScenario.analysisResult?.yearly_data) {
      if (debugEnabled) dlog('[Chart] No baseline or baseline has no data');
      // No baseline - return empty data
      return [];
    }

    const baselineData = baselineScenario.analysisResult.yearly_data;
    
    // Debug: Log baseline data structure
    if (debugEnabled) {
      dlog('[Chart] Baseline yearly_data structure:', {
        hasYears: !!baselineData.years,
        yearsLength: baselineData.years?.length || 0,
        yearsArray: baselineData.years,
        hasBuyingNW: !!baselineData.buying_net_worth,
        buyingNWLength: baselineData.buying_net_worth?.length || 0,
        hasRentingNW: !!baselineData.renting_net_worth,
        rentingNWLength: baselineData.renting_net_worth?.length || 0,
      });
    }

    for (let year = 1; year <= timeHorizonYears; year++) {
      const row: ChartDataPoint = { year };
      const baselineYears: number[] = baselineData.years || [];
      const baselineIdx = baselineYears.indexOf(year);
      
      visibleScenarios.forEach((scenario) => {
        const yd: any = scenario.analysisResult?.yearly_data;
        if (!yd) return;
        const years: number[] = yd.years || [];
        const idx = years.indexOf(year);
        
        if (scenario.isBaseline) {
          // Baseline always shows at 0
          row[scenario.id] = 0;
        } else {
          // Prefer server-provided relative values when available
          if (yd.relative_net_worth && Array.isArray(yd.relative_net_worth) && idx >= 0) {
            row[scenario.id] = yd.relative_net_worth[idx] ?? 0;
          } else {
            // Fallback: Calculate relative net worth vs baseline
            if (idx >= 0 && baselineIdx >= 0) {
              let scenarioNetWorth = 0;
              let baselineNetWorth = 0;

              if (scenario.scenarioType === 'buying') {
                scenarioNetWorth = (yd.buying_net_worth || [])[idx] || 0;
              } else {
                scenarioNetWorth = (yd.renting_net_worth || [])[idx] || 0;
              }

              if (baselineScenario.scenarioType === 'buying') {
                baselineNetWorth = (baselineData.buying_net_worth || [])[baselineIdx] || 0;
              } else {
                baselineNetWorth = (baselineData.renting_net_worth || [])[baselineIdx] || 0;
              }

              const relativeValue = scenarioNetWorth - baselineNetWorth;
              row[scenario.id] = relativeValue;
            } else {
              row[scenario.id] = 0;
            }
          }
        }
      });
      data.push(row);
    }
    return data;
  }, [
    timeHorizonYears,
    // Only depend on visible scenario IDs and their analysis results
    scenarios.filter(s => s.visible !== false && s.analysisResult?.yearly_data).map(s => s.id).join(','),
    scenarios.find(s => s.isBaseline)?.id || '',
    // Add hash of analysis results to detect actual data changes
    scenarios.filter(s => s.visible !== false && s.analysisResult?.yearly_data)
      .map(s => `${s.id}:${s.analysisResult?.final_buying_net_worth}:${s.analysisResult?.final_renting_net_worth}`)
      .join('|'),
  ]);

  // Phase 1.1: Log when chart data changes
  useEffect(() => {
    if (chartData.length > 0) {
      console.log('[CHART_DATA_CHANGE]', {
        dataPointCount: chartData.length,
        firstPoint: chartData[0],
        lastPoint: chartData[chartData.length - 1],
        timestamp: Date.now()
      });
    }
  }, [chartData]);

  // X axis ticks starting at year 1 (no year 0)
  const xTicks = useMemo(() => {
    if (timeHorizonYears <= 1) return [1];
    const maxTicks = Math.min(timeHorizonYears, 10);
    const step = Math.max(1, Math.floor(timeHorizonYears / maxTicks));
    const ticks: number[] = [];
    for (let t = 1; t <= timeHorizonYears; t += step) {
      ticks.push(t);
    }
    if (ticks[ticks.length - 1] !== timeHorizonYears) ticks.push(timeHorizonYears);
    return ticks;
  }, [timeHorizonYears]);

  // No break-even points needed since each scenario is either buying OR renting

  // Custom tooltip with scenario information
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg max-w-sm">
          <p className="font-semibold text-gray-900 mb-3">{`Year ${label}`}</p>

          {payload.map((entry: any, index: number) => {
            const key = String(entry.dataKey);
            const scenario = scenarios.find(s => s.id === key);
            const labelText = scenario ? scenario.title : key;

            return (
              <div key={index} className="flex items-center justify-between text-sm mb-2">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-gray-800 font-medium">{labelText}</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(entry.value)}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Compute Y-axis domain with headroom (including negative values)
  const { yMin, yMax } = useMemo(() => {
    let minVal = 0;
    let maxVal = 0;
    chartData.forEach((point) => {
      Object.keys(point).forEach((key) => {
        if (key !== 'year') {
          const val = point[key] as number;
          if (typeof val === 'number') {
            minVal = Math.min(minVal, val);
            maxVal = Math.max(maxVal, val);
          }
        }
      });
    });
    // Add ~20% headroom to ensure the final points are well within view
    const headroom = Math.max(Math.abs(minVal), maxVal) * 0.2;
    return {
      yMin: minVal - headroom,
      yMax: maxVal + headroom
    };
  }, [chartData]);

  // Create consistent Y ticks (nice rounding) including negative values
  const yTicks = useMemo(() => {
    if (yMax === yMin) return [0];
    
    // Calculate step size for nice rounding
    const range = yMax - yMin;
    const target = 6;
    const rawStep = range / target;
    
    // Round step to nearest 25k increment for better granularity
    const step = Math.max(25000, Math.round(rawStep / 25000) * 25000);
    
    // Calculate min and max ticks
    const minTick = Math.floor(yMin / step) * step;
    const maxTick = Math.ceil(yMax / step) * step;
    
    const ticks: number[] = [];
    for (let v = minTick; v <= maxTick; v += step) {
      ticks.push(v);
    }
    return ticks;
  }, [yMin, yMax]);

  // Consistent currency formatter (no unit switching)
  const formatYAxisCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value as number);
  };

  // Show empty chart structure even without data
  const hasData = chartData.length > 0 && scenarios.some(s => (s.visible !== false) && s.analysisResult?.yearly_data);

  // Create empty chart data for structure when no real data exists
  const displayData = hasData
    ? chartData
    : Array.from({ length: timeHorizonYears }, (_, i) => ({ year: i + 1 }));

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-md ${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Net Worth vs Baseline</h3>
            <p className="text-sm text-gray-600">
              Compare scenarios against the baseline. Positive values indicate better performance.
            </p>
          </div>

          {/* Export */}
          <div className="flex items-center space-x-3">
            {scenarios.some(s => s.analysisResult?.yearly_data) && (
              <button
                onClick={handleExport}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Export CSV
              </button>
            )}
          </div>
        </div>

        <div className="mb-4 relative" style={{ height: '520px' }}>
          {/* Show message overlay when no data */}
          {!hasData && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
              <div className="text-center text-gray-500">
                <p className="text-sm font-medium">
                  {scenarios.length === 0 
                    ? 'Add scenarios to see net worth comparison' 
                    : 'Set a baseline scenario to enable relative comparison'
                  }
                </p>
                <p className="text-xs mt-1">
                  {scenarios.length === 0 
                    ? 'Use the panel on the left' 
                    : 'Click "Set Baseline" on any scenario card'
                  }
                </p>
                <div className="mt-2 text-gray-400 select-none">⟵</div>
              </div>
            </div>
          )}

          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={displayData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />

              <XAxis
                dataKey="year"
                type="number"
                domain={[1, timeHorizonYears]}
                ticks={xTicks}
                tickFormatter={(value) => `${value}y`}
                label={{ value: 'Years', position: 'insideBottomRight', offset: -5 }}
              />

              <YAxis
                tickFormatter={(value) => formatYAxisCurrency(value as number)}
                width={100}
                label={{ value: 'Net Worth vs Baseline ($)', angle: -90, position: 'left', offset: -5 }}
                domain={[yTicks.length ? yTicks[0] : yMin, yTicks.length ? yTicks[yTicks.length - 1] : yMax]}
                ticks={yTicks}
              />

              <Tooltip content={hasData ? <CustomTooltip /> : undefined} />

              {/* Zero line reference - baseline */}
              <ReferenceLine 
                y={0} 
                stroke="#6b7280" 
                strokeDasharray="3 3" 
              />

              {hasData && (
                // Render one line per visible scenario
                scenarios
                  .filter(s => (s.visible !== false) && s.analysisResult?.yearly_data)
                  .map((scenario, i) => {
                    const color = SCENARIO_COLORS[i % SCENARIO_COLORS.length];
                    return (
                      <Line
                        key={scenario.id}
                        type="linear"
                        dataKey={scenario.id}
                        stroke={color}
                        strokeWidth={3}
                        dot={{ r: 3, fill: color, stroke: '#fff', strokeWidth: 1 }}
                        activeDot={{ r: 6, fill: color }}
                        name={`${scenario.title} (${scenario.scenarioType})`}
                      />
                    );
                  })
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legend removed; toggles are in Scenario cards */}
    </div>
  );
};
